import { useContext } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FaArrowRight } from "react-icons/fa6";

import StatCard from "@/components/cards/statCard";
import ContentPage from "@/components/contentPage";
import {
  getVisibleNavItems,
  getVisibleNavSections,
} from "@/components/navConfig";
import UploadHistoryPanel from "@/components/workspace/uploadHistoryPanel";
import WorkspacePanel from "@/components/workspace/workspacePanel";
import UserContext from "@/contexts/user";
import useHomeDashboard from "@/hooks/useHomeDashboard";

export const Route = createFileRoute("/")({
  component: HomePage,
});

function HomePage() {
  return (
    <ContentPage title="Mako">
      <HomeDashboard />
    </ContentPage>
  );
}

function HomeDashboard() {
  const { user } = useContext(UserContext);
  const { metrics } = useHomeDashboard(user);

  // Build quick actions from the same nav config the sidebar uses, so links
  // are always real, permission-aware, and never point at "coming soon" pages.
  const quickActions = getVisibleNavSections(user).flatMap((section) =>
    getVisibleNavItems(section, user).flatMap((item) => {
      const { to } = item;
      if (!to || to === "/" || item.comingSoon) {
        return [];
      }
      return [{ key: item.key, title: item.title, to }];
    })
  );

  return (
    <div className="space-y-6">
      <p className="text-pretty text-center text-sm text-grey-200">
        OceanX Mission Data Service — operational overview of expeditions and
        data readiness.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <StatCard
            key={metric.key}
            label={metric.label}
            value={metric.value}
            detail={metric.detail}
            tone={metric.tone}
            isLoading={metric.isLoading}
            isError={metric.isError}
          />
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-5">
        <WorkspacePanel
          className="xl:col-span-2"
          title="Quick Actions"
          subtitle="Jump into common dashboards and workflows"
        >
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
            {quickActions.map((action) => (
              <Link
                key={action.key}
                to={action.to}
                className="group flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
              >
                <span className="group-hover:underline">{action.title}</span>
                <FaArrowRight
                  className="shrink-0 -translate-x-2 text-blue-200 opacity-0 transition-[opacity,transform] duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100"
                  aria-hidden="true"
                />
              </Link>
            ))}
          </div>
        </WorkspacePanel>

        <UploadHistoryPanel className="xl:col-span-3" />
      </div>
    </div>
  );
}
